<script lang="ts">
  import Fa from 'svelte-fa';
  import { faAngleDown, faAngleRight, faAngleUp } from '@fortawesome/free-solid-svg-icons';

  import { testThresholds, type TableRow } from '#lib/zod/output/TabularData.js';
  import Checkbox from '../Checkbox.svelte';
  import { mapOpt, optToArr } from '#lib/overview/utils/object.js';
  import {
    aggregateRows,
    alertIcon,
    calculateCountHint,
    firstOrdinaryRowIndex,
    formatDayjs,
    formatThresholdTooltip,
    formatUnit,
    formatValue,
    peakIconAsRawHtml,
  } from './utils';
  import { DataTableContextKey } from './types';
  import { untrack } from 'svelte';
  import { DebugNowContextKey } from '../../../routes/(admin)/+layout.svelte';
  import { getContext } from '#lib/utils/svelte-context.js';
  import type { ClassValue } from 'svelte/elements';

  const now = getContext(DebugNowContextKey);
  const ctx = getContext(DataTableContextKey);

  const daysOld = (d: Readonly<Date>): number => {
    const n = now() ?? Date.now();
    return Math.floor((n - +d) / (24 * 60 * 60_000));
  };

  interface Props {
    row: TableRow;
    rowClass?: ClassValue;
    rowStyle?: string;
    cellClass?: ClassValue;
    cellStyle?: string;
    groupHeader?: boolean;
    selected?: boolean | null;
    selectable?: boolean;
    nameOnly?: boolean;
    alert?: 0 | 1 | 2 | 3;
    rowHref?: (r: TableRow) => string | undefined;
    level?: number;
    expanded?: boolean;
    childrenLoading?: boolean;
    onExpand?: (value: boolean) => void;
    onSelect?: (selected: boolean) => void;
    virtualIndex?: number;
    rowElementRef?: HTMLTableRowElement;
  }

  let {
    row,
    rowClass,
    rowStyle,
    cellClass,
    cellStyle,
    groupHeader,
    selected,
    selectable = true,
    nameOnly,
    alert,
    rowHref,
    level = 0,
    expanded,
    childrenLoading,
    onExpand,
    onSelect,
    virtualIndex,
    rowElementRef = $bindable(undefined),
  }: Props = $props();

  const {
    flatCols: cols,
    borders,
    hasAlerts,
    showCheckboxes,
    tooltip,
    rowSettings,
  } = $derived(ctx);

  const {
    nameFunction,
    hidePeak,
    countHint,
    countHintTooltip,
    showGroupHeaderAlerts,
    rowNameBold,
    onRowClick,
  } = $derived(rowSettings);

  let href = $derived(rowHref?.(row));

  let firstColspan = $derived(firstOrdinaryRowIndex(true, showCheckboxes, hasAlerts));
  let missingCols = $derived(
    cols.filter((c) =>
      !(c.type === 'data' || !c.type) ? false : !Object.keys(row.data ?? {}).includes(c.key),
    ),
  );
  let data = $derived(
    !('children' in row && row.children !== undefined) || missingCols.length === 0
      ? row.data
      : { ...row.data, ...aggregateRows(missingCols, row.children, 'grouping').data },
  );

  let countHintContent = $derived.by<{ subgroups?: number; leafs: number } | undefined>(() => {
    if ('countHint' in row && row.countHint) {
      return row.countHint;
    } else if (row.type === 'group') {
      return untrack(() => calculateCountHint(row));
    }

    return undefined;
  });

  const thickBorder = 'not-last:border-r-2';
</script>

<tr
  bind:this={rowElementRef}
  class={[rowClass, virtualIndex !== undefined && 'virtualized']}
  style={rowStyle}
  onclick={() => onRowClick?.(row)}
  data-index={virtualIndex}
>
  {#if showCheckboxes}
    <td onclick={(e) => e.stopPropagation()}>
      {#if selectable}
        <Checkbox {selected} {onSelect} />
      {/if}
    </td>
  {/if}
  {#if hasAlerts}
    <td class="threshold-alert" data-severity={alert}>
      {#if alert}<Fa icon={alertIcon(alert)} />{/if}
    </td>
  {/if}
  <td
    class={[
      // safelist: pl-6! pl-12! pl-18! pl-20!
      `pl-${Math.min(level * 6, 20)}!`,
      cellClass,
      groupHeader || rowNameBold ? 'font-bold' : undefined,
      borders?.has(firstColspan) && thickBorder,
    ]}
    style={cellStyle}
  >
    {#if groupHeader}
      {#if childrenLoading}
        <span class="loading loading-spinner text-primary loading-xs mr-0.5"></span>
      {:else}
        <button class="px-1 align-middle" onclick={() => onExpand?.(!expanded)}>
          <Fa icon={expanded ? faAngleDown : faAngleRight} />
        </button>
      {/if}
    {/if}
    {#snippet name()}
      {#if nameFunction}
        {@render nameFunction(row, level)}
      {:else}
        {@html row.name}
      {/if}
    {/snippet}
    {#if href !== undefined}
      <a class="hover:underline" {href}>{@render name()}</a>
    {:else}
      {@render name()}
    {/if}
    {#if countHint && countHintContent}
      <span
        role="tooltip"
        onmouseleave={() => tooltip.set(undefined)}
        onmouseenter={() =>
          tooltip.set(
            countHintTooltip === undefined
              ? undefined
              : typeof countHintTooltip === 'string'
                ? { text: countHintTooltip }
                : {
                    text: countHintTooltip.find((x) => x.level === level)?.text ?? '',
                  },
          )}
        class="text-xs opacity-60"
      >
        {#if countHint === 'leafs' || countHintContent.subgroups === undefined}
          ({countHintContent.leafs})
        {:else if countHint === 'subgroups'}
          ({countHintContent.subgroups})
        {:else if countHint === 'both'}
          ({countHintContent.leafs} in {countHintContent.subgroups} subgroups)
        {/if}
      </span>
    {/if}
  </td>
  {#each cols as col, i}
    {@const title = `${col.title ?? (col as any).key}`}
    {@const d = 'key' in col && col.key !== undefined ? data?.[col.key] : undefined}
    {@const howOld = mapOpt(d?.date, daysOld)}
    {@const isOld = mapOpt(howOld, (x) => x >= 2)}
    {@const oldDesc = isOld ? `${howOld} days old` : undefined}
    {@const severity =
      d?.value && 'thresholds' in col && col.thresholds
        ? testThresholds(col.thresholds, d.value)
        : undefined}
    {@const alertDesc =
      'thresholds' in col
        ? mapOpt(
            col.thresholds?.find((t) => t.urgency === severity),
            (t) => formatThresholdTooltip(t, col),
          )
        : undefined}
    {@const titleDesc = [...optToArr(col.group?.title), title].join(' ')}
    {@const desc =
      'tooltipText' in col && col.tooltipText !== undefined
        ? [col.tooltipText]
        : [titleDesc, ...optToArr(oldDesc), ...optToArr(alertDesc)]}
    {@const format = !col.type || col.type === 'data' ? col.numberFormat : undefined}

    <td
      class={[borders?.has(i + firstColspan + 1) && thickBorder, isOld && 'old']}
      data-column={'key' in col ? col.key : undefined}
      onmouseleave={() => tooltip.set(undefined)}
      onmouseenter={() =>
        tooltip.set(
          'customCellTooltip' in col && col.customCellTooltip
            ? {
                snippet: col.customCellTooltip,
                props: [row, col],
              }
            : {
                text: desc.join('<br />'),
              },
        )}
    >
      {#if nameOnly}
        <!-- eslint-disable-next-line svelte/no-useless-mustaches -->
        {''}
      {:else if (col.type === 'data' || col.type === undefined) && d}
        {#if isNaN(d.value)}
          —
        {:else if col.isPrediction}
          {#if d.value < 0 || d.value > 365}
            <div>≥ 1 year</div>
          {:else}
            <div>{d.value.toFixed(0)} days</div>
          {/if}
        {:else}
          <div class="flex justify-end gap-1">
            {#if severity && (showGroupHeaderAlerts || !groupHeader)}
              <span class="threshold-alert" data-severity={severity}>
                <Fa icon={alertIcon(severity)} />
              </span>
            {/if}
            <span>{formatValue(d.value, format)}</span>
            <span>{formatUnit(d.units ?? '')}</span>
          </div>
          {#if d.peak !== undefined && !hidePeak}
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="flex justify-end gap-1 text-xs"
              onmouseleave={() => tooltip.set(undefined)}
              onmouseenter={(e) => {
                e.stopPropagation();
                tooltip.set({
                  text: `${peakIconAsRawHtml()} Peak ${desc.join('<br />')}`,
                });
              }}
            >
              <span class="inline-block self-center align-baseline text-red-500">
                <Fa icon={faAngleUp} size="xs" />
              </span>
              <span>{formatValue(d.peak, format)}</span>
              <span>{formatUnit(d.units ?? '')}</span>
            </div>
          {/if}
        {/if}
      {:else if col.type === 'parameter'}
        {@const p = row.parameters[col.parameter]}
        {col.render ? col.render(p) : (p ?? '-')}
      {:else if col.type === 'date-like-parameter'}
        {@const p = row.parameters[col.parameter]}
        {p !== undefined ? formatDayjs(p, col.displayFormat) : '—'}
      {:else if col.type === 'hasTag'}
        <div class="text-center!">
          <input
            type="checkbox"
            class="checkbox checkbox-xs checkbox-primary cursor-default"
            onclick={(e) => e.preventDefault()}
            checked={row.tags.includes(col.tag)}
          />
        </div>
      {:else if col.type === 'custom'}
        {@render col.snippet(row, col.props)}
      {:else}
        -
      {/if}
    </td>
  {/each}
</tr>

<style>
  .old {
    color: red;
  }

  .virtualized {
    box-sizing: border-box;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    display: grid;
    grid-template-columns: var(--grid-col-template);
  }
</style>
